import { ApiError, ERROR_MESSAGES, ErrorType } from '@/types/errors';

/**
 * Extracts resource context from URL pathname
 */
export function extractResourceContext(pathname: string): {
  customerId?: string;
  projectId?: string;
  farmId?: string;
  fieldId?: string;
  userId?: string;
} {
  // Pattern: /customers/:customerId/projects/:projectId/farms/:farmId/fields/:fieldId
  const fullPattern = pathname.match(/\/customers\/([^/]+)\/projects\/([^/]+)\/farms\/([^/]+)\/fields\/([^/]+)/);
  if (fullPattern) {
    return {
      customerId: fullPattern[1],
      projectId: fullPattern[2],
      farmId: fullPattern[3],
      fieldId: fullPattern[4],
    };
  }

  // Pattern: /customers/:customerId/projects/:projectId/farms/:farmId
  const farmPattern = pathname.match(/\/customers\/([^/]+)\/projects\/([^/]+)\/farms\/([^/]+)/);
  if (farmPattern) {
    return {
      customerId: farmPattern[1],
      projectId: farmPattern[2],
      farmId: farmPattern[3],
    };
  }

  // Pattern: /customers/:customerId/projects/:projectId
  const projectPattern = pathname.match(/\/customers\/([^/]+)\/projects\/([^/]+)/);
  if (projectPattern) {
    return {
      customerId: projectPattern[1],
      projectId: projectPattern[2],
    };
  }

  // Pattern: /customers/:customerId
  const customerPattern = pathname.match(/\/customers\/([^/]+)/);
  if (customerPattern) {
    return {
      customerId: customerPattern[1],
    };
  }

  // Pattern: /users/edit/:userId
  const userEditPattern = pathname.match(/\/users\/edit\/([^/]+)/);
  if (userEditPattern) {
    return {
      userId: userEditPattern[1],
    };
  }

  return {};
}

/**
 * Normalizes various error formats into a consistent ApiError format
 */
export function normalizeError(error: unknown): ApiError {
  // Already an API error
  if (error && typeof error === 'object' && 'status' in error && 'message' in error) {
    return error as ApiError;
  }

  // JavaScript Error
  if (error instanceof Error) {
    return {
      status: undefined,
      message: error.message || 'An unexpected error occurred.',
      code: 'unknown_error',
    };
  }

  // String error
  if (typeof error === 'string') {
    return {
      status: undefined,
      message: error,
      code: 'unknown_error',
    };
  }

  // React Router Error Response
  if (error && typeof error === 'object' && 'status' in error && 'statusText' in error) {
    const routerError = error as { status: number; statusText: string; data?: unknown };
    return {
      status: routerError.status,
      message: routerError.statusText,
      code: 'route_error',
    };
  }

  // Promise rejection with just a message
  if (error && typeof error === 'object' && 'message' in error) {
    return {
      status: undefined,
      message: (error as { message: string }).message,
      code: 'unknown_error',
    };
  }

  // Network or fetch errors
  if (error && typeof error === 'object' && 'name' in error) {
    const namedError = error as { name: string; message?: string };
    if (namedError.name === 'NetworkError' || namedError.name === 'TypeError') {
      return {
        status: 0,
        message: namedError.message || 'Network error occurred.',
        code: 'network_error',
      };
    }
  }

  // Completely unknown error
  return {
    status: undefined,
    message: 'An unexpected error occurred.',
    code: 'unknown_error',
  };
}

/**
 * Determines the appropriate error type from an ApiError
 */
export function getErrorTypeFromApiError(apiError: ApiError): ErrorType {
  // Use backend error code if available
  if (apiError.code) {
    switch (apiError.code) {
      case 'not_found':
      case 'route_error':
        return ErrorType.NOT_FOUND;
      case 'authentication_failed':
        return ErrorType.AUTHENTICATION_FAILED;
      case 'permission_denied':
        return ErrorType.PERMISSION_DENIED;
      case 'validation_error':
      case 'invalid':
        return ErrorType.VALIDATION_ERROR;
      case 'timeout':
        return ErrorType.TIMEOUT;
      case 'network_error':
        return ErrorType.NETWORK_ERROR;
      case 'internal_error':
        return ErrorType.INTERNAL_SERVER_ERROR;
      case 'conflict':
        return ErrorType.CONFLICT;
      default:
        break;
    }
  }

  // Use HTTP status code
  if (apiError.status) {
    switch (apiError.status) {
      case 400:
        return ErrorType.BAD_REQUEST;
      case 401:
        return ErrorType.UNAUTHORIZED;
      case 403:
        return ErrorType.FORBIDDEN;
      case 404:
        return ErrorType.NOT_FOUND;
      case 409:
        return ErrorType.CONFLICT;
      case 500:
      case 502:
      case 503:
        return ErrorType.INTERNAL_SERVER_ERROR;
      case 504:
        return ErrorType.TIMEOUT;
      default:
        break;
    }
  }

  // Network error
  if (apiError.status === 0 || apiError.code === 'network_error') {
    return ErrorType.NETWORK_ERROR;
  }

  // Default fallback
  return ErrorType.UNKNOWN_ERROR;
}

/**
 * Safely handles any error type and provides appropriate user messaging
 */
export function handleFallbackError(error: unknown): {
  errorType: ErrorType;
  message: string;
  statusCode?: number;
} {
  try {
    const normalizedError = normalizeError(error);
    const errorType = getErrorTypeFromApiError(normalizedError);

    // Note: Error logging is handled by backend and Sentry integration

    // Get user-friendly message with robust fallbacks
    const errorInfo = ERROR_MESSAGES[errorType];
    const message = normalizedError.message || errorInfo.description || 'An unexpected error occurred.';

    return {
      errorType,
      message,
      statusCode: normalizedError.status,
    };
  } catch (handlerError) {
    // Even the fallback handler failed - this is a critical error
    console.error('Fallback error handler failed:', handlerError);

    // Absolute last resort with good fallback message
    return {
      errorType: ErrorType.UNKNOWN_ERROR,
      message: 'A critical error occurred. Please refresh the page or contact support.',
    };
  }
}

/**
 * Global error handler for uncaught errors
 */
export function setupGlobalErrorHandler() {
  // Handle uncaught JavaScript errors
  window.addEventListener('error', (event) => {
    const error = event.error || new Error(event.message);
    handleFallbackError(error);
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleFallbackError(event.reason);
    // Let default handler proceed for console logging
  });
}

/**
 * Check if an error is recoverable (user can retry)
 */
export function isRecoverableError(error: ApiError): boolean {
  // Network errors are usually recoverable
  if (error.status === 0 || error.code === 'network_error') {
    return true;
  }

  // Timeout errors are recoverable
  if (error.status === 504 || error.code === 'timeout') {
    return true;
  }

  // Some server errors might be temporary
  if (error.status === 500 || error.status === 502 || error.status === 503) {
    return true;
  }

  // Client errors are usually not recoverable
  if (error.status && error.status >= 400 && error.status < 500) {
    return false;
  }

  // Unknown errors - let user try
  return true;
}
